// Graphs view (Dev Console): the graphs of sessions the backend runs —
// fanout (tasks → workers), arena (one ask, several model families, a judge
// picks), review (verifiers + a judge from another family → the merge
// gate). Left: the list; right: the selected graph as a React Flow diagram,
// one card per node coloured by status, edges typed (spawns / verifies /
// judges / blocks), and the judge's verdict. Click a node to open its
// session. Start a graph from the form at the top.
//
// Data: GET /dev/graphs (+ /dev/graphs/:id), live-refreshed every 10 s
// while a graph runs. shared/devOps/graph.js is the shape.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactFlow, ReactFlowProvider, Background, Controls, Handle, Position, MarkerType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { kitEnv } from '../../data/kitEnv.js';
import { getIdToken } from '../../data/authPlane';
import './GraphPanel.css';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');
async function api(path, body) {
  const token = await getIdToken();
  const res = await fetch(`${API}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `${path}: ${res.status}`);
  return json;
}

const ago = (ms) => { if (!ms) return '—'; const m = Math.round((Date.now() - ms) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`; };
const KIND_ICON = { coordinator: '◎', worker: '▣', verifier: '◉', judge: '⚖', gardener: '✿' };
const STATUS_TONE = { pending: 'gray', launching: 'blue', running: 'blue', done: 'green', failed: 'red', skipped: 'gray' };
const GRAPH_TONE = { running: 'blue', cancelling: 'amber', done: 'green', failed: 'red', cancelled: 'gray' };
const NODE_W = 220;
const NODE_H = 64;

// Layout: columns by depth along non-blocking edges (coordinator → workers
// / verifiers → judge), rows within a column.
export function layoutGraph(graph) {
  const nodes = graph?.nodes || [];
  const edges = (graph?.edges || []).filter((e) => e.type !== 'blocks');
  const depth = {};
  const incoming = (id) => edges.filter((e) => e.to === id).map((e) => e.from);
  const visit = (id, seen = new Set()) => {
    if (depth[id] != null) return depth[id];
    if (seen.has(id)) return 0;
    seen.add(id);
    const ups = incoming(id);
    depth[id] = ups.length ? Math.max(...ups.map((u) => visit(u, seen))) + 1 : 0;
    return depth[id];
  };
  nodes.forEach((n) => visit(n.id));
  const cols = {};
  nodes.forEach((n) => { (cols[depth[n.id]] ||= []).push(n); });
  const flowNodes = [];
  Object.entries(cols).forEach(([d, list]) => {
    const total = list.length;
    list.forEach((n, i) => {
      flowNodes.push({
        id: n.id, type: 'graphNode', position: { x: Number(d) * (NODE_W + 80), y: (i - (total - 1) / 2) * (NODE_H + 28) },
        data: { node: n }, draggable: false,
      });
    });
  });
  const flowEdges = (graph?.edges || []).map((e, i) => ({
    id: `e${i}`, source: e.from, target: e.to, type: 'smoothstep', label: e.type,
    className: `gp-edge gp-edge-${e.type}`, animated: e.type !== 'blocks' && nodes.find((n) => n.id === e.to)?.status === 'running',
    markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
    style: e.type === 'blocks' ? { strokeDasharray: '4 4' } : undefined,
  }));
  return { nodes: flowNodes, edges: flowEdges };
}

function GraphNode({ data, selected }) {
  const n = data.node;
  const verdict = n.verdictOk === true ? 'pass' : n.verdictOk === false ? 'fail' : null;
  return (
    <div className={`gp-card tone-${STATUS_TONE[n.status] || 'gray'}${selected ? ' selected' : ''}`} title={n.reason || n.label || n.id}>
      <Handle type="target" position={Position.Left} className="gp-handle" />
      <Handle type="source" position={Position.Right} className="gp-handle" />
      <div className="gp-card-head">
        <span className="gp-icon" aria-hidden="true">{KIND_ICON[n.kind] || '○'}</span>
        <span className="gp-name u-truncate">{n.label || n.id}</span>
        {['running', 'launching'].includes(n.status) ? <span className="devc-pulse" /> : <span className={`cs-dot gp-dot tone-${STATUS_TONE[n.status] || 'gray'}`} />}
      </div>
      <div className="gp-card-foot">
        <span className="gp-sub u-truncate">{n.kind}{n.model ? ` · ${n.model}` : ''}{n.directive ? ` · ${n.directive}` : ''}</span>
        {verdict && <span className={`devc-chip ${verdict === 'pass' ? 'green' : 'red'}`}>{verdict}</span>}
        {n.prUrl && <span className="devc-chip gray">PR</span>}
      </div>
    </div>
  );
}
const nodeTypes = { graphNode: GraphNode };

function GraphCanvas({ graph, onOpenSession }) {
  const { nodes, edges } = useMemo(() => layoutGraph(graph), [graph]);
  const note = `Graph "${graph.title}" (${graph.template}, ${graph.status}): ${(graph.nodes || []).map((n) => `${n.label || n.id} ${n.status}`).join(', ')}.${graph.verdict?.summary ? ` Verdict: ${graph.verdict.summary}` : ''}`;
  return (
    <div className="gp-canvas" data-nova-note={note}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.3, maxZoom: 1.1 }}
          minZoom={0.3}
          maxZoom={1.6}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          onNodeClick={(_, n) => { const s = n.data?.node?.sessionId; if (s) onOpenSession?.(s); }}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={24} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}

function StartForm({ sessions, onStarted }) {
  const [template, setTemplate] = useState('review');
  const [rootId, setRootId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [tasks, setTasks] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const prSessions = useMemo(() => (sessions || []).filter((s) => s.prUrl && !s.prMerged && s.kind !== 'verifier' && s.kind !== 'judge').slice(0, 30), [sessions]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const body = { template };
      if (template === 'review') body.rootId = rootId;
      if (template === 'arena') body.prompt = prompt;
      if (template === 'fanout') body.tasks = tasks.split('\n').map((l) => l.replace(/^[-*\d.)\s]+/, '').trim()).filter(Boolean).map((t) => ({ title: t.slice(0, 80), prompt: t }));
      const out = await api('/dev/graphs', body);
      setPrompt(''); setTasks('');
      onStarted?.(out.id);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <form className="gp-start" onSubmit={submit} data-nova-note="Start a graph: review a PR with verifiers and a judge, run an arena of model families on one ask, or fan a task list out to parallel workers">
      <div className="gp-start-row">
        <div className="cs-seg" role="tablist" aria-label="Graph template">
          {[['review', 'Review a PR', 'Independent verifiers drive the change; a judge from another model family decides whether it may merge'], ['arena', 'Arena', 'The same ask to several model families; a judge picks the base to continue from'], ['fanout', 'Fan out', 'One task per line → one worker each, in parallel']].map(([id, label, tip]) => (
            <button key={id} type="button" role="tab" aria-selected={template === id} className={`devc-filter${template === id ? ' active' : ''}`} title={tip} onClick={() => setTemplate(id)}>{label}</button>
          ))}
        </div>
      </div>
      {template === 'review' && (
        <select className="cs-input" value={rootId} onChange={(e) => setRootId(e.target.value)} required aria-label="Session with the pull request to review">
          <option value="">Pick a session with an open PR…</option>
          {prSessions.map((s) => <option key={s.id} value={s.id}>{s.title || s.id}</option>)}
        </select>
      )}
      {template === 'arena' && <textarea className="cs-input" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="The ask every candidate gets…" required />}
      {template === 'fanout' && <textarea className="cs-input" rows={4} value={tasks} onChange={(e) => setTasks(e.target.value)} placeholder={'One task per line, e.g.\n- Add the export button to the items list\n- Write the CSV endpoint behind it'} required />}
      <div className="gp-start-row">
        <button type="submit" className="devc-btn primary" disabled={busy} data-agent-guard>{busy ? 'Starting…' : `Start ${template}`}</button>
        {err && <span className="devc-err">{err}</span>}
      </div>
    </form>
  );
}

export default function GraphPanel({ sessions, onOpenSession, initialId = null }) {
  const [graphs, setGraphs] = useState([]);
  const [selectedId, setSelectedId] = useState(initialId);
  const [graph, setGraph] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    try { const out = await api('/dev/graphs?limit=40'); setGraphs(out.graphs || []); setErr(null); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (initialId) setSelectedId(initialId); }, [initialId]);
  useEffect(() => {
    if (!selectedId) { setGraph(null); return undefined; }
    let alive = true;
    const pull = () => api(`/dev/graphs/${selectedId}`).then((g) => { if (alive) setGraph(g); }).catch((e) => { if (alive) setErr(e.message); });
    pull();
    const t = setInterval(() => { pull(); load(); }, 10 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [selectedId, load]);
  const cancel = async () => {
    if (!graph) return;
    try { await api(`/dev/graphs/${graph.id}/cancel`, {}); await load(); } catch (e) { setErr(e.message); }
  };
  const live = (graph?.nodes || []).filter((n) => n.kind !== 'coordinator');
  const count = (st) => live.filter((n) => n.status === st).length;
  return (
    <div className="gp-wrap" data-nova-note="Graphs: the fan-out, arena and review graphs of agent sessions this instance ran; pick one to see its nodes, edges and the judge's verdict">
      <StartForm sessions={sessions} onStarted={(id) => { setSelectedId(id); load(); }} />
      {err && <div className="devc-err">{err}</div>}
      <div className="gp-body">
        <aside className="gp-list" aria-label="Graphs">
          {graphs.map((g) => {
            const l = (g.nodes || []).filter((n) => n.kind !== 'coordinator');
            return (
              <button type="button" key={g.id} className={`devc-sess${selectedId === g.id ? ' active' : ''}`} onClick={() => setSelectedId(g.id)}>
                <div className="devc-sess-title">{['running', 'cancelling'].includes(g.status) && <span className="devc-pulse" />}{g.title}</div>
                <div className="devc-sess-meta">
                  <span className={`devc-chip ${GRAPH_TONE[g.status] || 'gray'}`}>{g.status}</span>
                  <span className="devc-chip gray">{g.template}</span>
                  <span className="devc-dim">{l.filter((n) => n.status === 'done').length}/{l.length} · {ago(g.updatedAt)}</span>
                </div>
              </button>
            );
          })}
          {!graphs.length && <div className="devc-dim" style={{ padding: 10 }}>No graphs yet — start one above.</div>}
        </aside>
        <section className="gp-detail">
          {!graph && <div className="cs-empty">Pick a graph to see its nodes.</div>}
          {graph && (
            <>
              <header className="gp-detail-head">
                <div>
                  <h3 className="gp-title">{graph.title}</h3>
                  <div className="devc-sess-meta">
                    <span className={`devc-chip ${GRAPH_TONE[graph.status] || 'gray'}`}>{graph.status}</span>
                    <span className="devc-chip gray">{graph.template}</span>
                    <span className="devc-dim">{count('done')} done · {count('running') + count('launching')} running · {count('failed')} failed · started {ago(graph.createdAt)}</span>
                  </div>
                </div>
                <div className="gp-actions" data-agent-guard>
                  {graph.rootId && <button type="button" className="devc-filter" onClick={() => onOpenSession?.(graph.rootId)}>Open root session</button>}
                  {['running', 'cancelling'].includes(graph.status) && <button type="button" className="devc-btn danger" onClick={cancel} data-agent-noclick>Cancel graph</button>}
                </div>
              </header>
              <GraphCanvas graph={graph} onOpenSession={onOpenSession} />
              {graph.verdict && (
                <section className={`gp-verdict tone-${graph.verdict.ok === false ? 'red' : graph.verdict.ok === true || graph.verdict.pick ? 'green' : 'gray'}`} data-nova-note="The judge's verdict for this graph">
                  <strong>{graph.template === 'arena' ? `Judge picked ${graph.verdict.pick || 'nothing'}` : graph.verdict.ok ? 'Judge approved the merge' : 'Judge blocked the merge'}</strong>
                  {graph.verdict.headSha && <span className="devc-dim"> @ {String(graph.verdict.headSha).slice(0, 7)}</span>}
                  {graph.verdict.summary && <p>{graph.verdict.summary}</p>}
                  {graph.verdict.sessionId && <button type="button" className="devc-filter" onClick={() => onOpenSession?.(graph.verdict.sessionId)}>Open the judge</button>}
                </section>
              )}
              <ul className="gp-nodes">
                {(graph.nodes || []).map((n) => (
                  <li key={n.id}>
                    <span className={`cs-dot gp-dot tone-${STATUS_TONE[n.status] || 'gray'}`} />
                    <span className="gp-node-label">{KIND_ICON[n.kind]} {n.label || n.id}</span>
                    <span className="devc-dim">{n.status}{n.reason ? ` — ${n.reason}` : ''}</span>
                    {n.sessionId && <button type="button" className="devc-filter" onClick={() => onOpenSession?.(n.sessionId)}>Open</button>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
